import React, { useEffect, useState } from 'react';
import { Form, redirect, useNavigation } from 'react-router-dom';
import { useDashboardContext } from '../../pages/DashboardLayout';
import { toast } from 'react-toastify';
import customFetch from '../../utils/customFetch';
import { useQuery } from '@tanstack/react-query';

const addressQuery = {
  queryKey: ['address'],
  queryFn: async () => {
    const data = await customFetch.get('/address');
    return data;
  },
};

export const loader = (queryClient) => async () => {
  const { data } = await queryClient.ensureQueryData(addressQuery);
  return data;
};

// const secondaryNavigation = [
//   { name: 'Account', href: '#', current: true },
//   { name: 'Benachrichtigungen', href: '#', current: false },
// ];

const secondaryNavigation = [{ name: 'Account', href: '#', current: true }];

export const action =
  (queryClient) =>
  async ({ request }) => {
    const formData = await request.formData();
    // Switch für Forms
    const intent = formData.get('intent');

    if (intent === 'allgemeine_einstellungen') {
      const file = formData.get('avatar');

      if (file && file.size > 5000000) {
        toast.error('Das Bild darf nicht grösser als 5MB sein');
        return null;
      }

      try {
        await customFetch.patch('/users/update-user', formData);
        queryClient.invalidateQueries(['user']);
        toast.success('Erfolgreich angepasst!');
        return redirect('/dashboard/einstellungen');
      } catch (error) {
        toast.error(error?.response?.data?.msg);
        return error;
      }
    } else if (intent === 'lieferadresse') {
      const data = Object.fromEntries(formData);
      try {
        if (data.existingAddress) {
          await customFetch.post('/address', data);
        } else {
          await customFetch.patch('/address', data);
        }
        toast.success('Adresse gespeichert!');
        queryClient.invalidateQueries(['address']);
        return redirect('/dashboard/einstellungen');
      } catch (error) {
        toast.error(error?.response?.data?.msg);
        return error;
      }
    }
  };

const Settings = () => {
  const { user } = useDashboardContext();
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [userAddress, setUserAddress] = useState([]);
  const data = useQuery(addressQuery).data.data;
  useEffect(() => {
    if (data.userAddress.length !== 0) {
      setUserAddress(data.userAddress[0]);
    }
  }, []);

  // Image Preview
  const [selectedFile, setSelectedFile] = useState();
  const [preview, setPreview] = useState();
  // create a preview as a side effect, whenever selected file is changed
  useEffect(() => {
    if (!selectedFile) {
      setPreview(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);

    // free memory when ever this component is unmounted
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const onSelectFile = (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      setSelectedFile(undefined);
      return;
    }

    // I've kept this example simple by using the first image instead of multiple
    setSelectedFile(e.target.files[0]);
  };

  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';

  // Passwort zurücksetzen
  const handlePasswordReset = async () => {
    setButtonDisabled(true);
    try {
      await customFetch.post('/auth/forgot-password', {
        email: user.email,
      });
    } catch (error) {
      toast.error(
        'Es gab ein Problem beim Senden des Links. Bitte versuche es erneut.'
      );
      console.error('Error during password reset:', error.message);
    }
  };

  return (
    <main>
      <header className="border-b border-white/5">
        {/* Secondary navigation */}
        <nav className="flex overflow-x-auto py-4">
          <ul
            role="list"
            className="flex min-w-full flex-none gap-x-6 px-4 text-sm font-semibold leading-6 text-gray-700 sm:px-6 lg:px-8"
          >
            {secondaryNavigation.map((item) => (
              <li key={item.name}>
                <a
                  href={item.href}
                  className={item.current ? 'text-svisit-primary-400' : ''}
                >
                  {item.name}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      {/* Settings forms */}
      <div className="divide-y divide-gray/5">
        <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Persönliche Informationen
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-700">
              Name, Titel und E-Mail-Adresse verwalten. Die Felder "Vorname",
              "Name", "Titel / Jobbezeichnung" sowie "Unternehmen" erscheinen
              als fixe Felder auf deiner Profilseite.
            </p>
          </div>

          <Form
            method="POST"
            className="md:col-span-2"
            encType="multipart/form-data"
          >
            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:max-w-xl sm:grid-cols-6">
              <div className="col-span-full flex items-center gap-x-8">
                {selectedFile ? (
                  <img
                    src={preview}
                    alt="avatar"
                    className="h-24 w-24 flex-none rounded-lg bg-gray-800 object-cover"
                  />
                ) : (
                  <img
                    src={user.avatar}
                    alt="avatar"
                    className="h-24 w-24 flex-none rounded-lg bg-gray-800 object-cover"
                  />
                )}

                <div>
                  <button
                    type="button"
                    className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs hover:bg-gray-50 ring-1 ring-inset ring-gray-300"
                  >
                    <label htmlFor="avatar" className="relative cursor-pointer">
                      Profilbild wechseln
                    </label>
                    <input
                      id="avatar"
                      name="avatar"
                      type="file"
                      accept=".png, .jpg, .jpeg"
                      className="sr-only"
                      onChange={onSelectFile}
                    />
                  </button>
                  <p className="mt-2 text-xs leading-5 text-gray-700">
                    JPG oder PNG. 5MB max.
                  </p>
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Vorname
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="firstName"
                    id="firstName"
                    autoComplete="given-name"
                    className="capitalize block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300  focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={user.firstName}
                  />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Nachname
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="lastName"
                    id="lastName"
                    autoComplete="family-name"
                    className="capitalize block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={user.lastName}
                  />
                </div>
              </div>

              <div className="col-span-full">
                <label
                  htmlFor="title"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Titel / Jobbezeichnung
                </label>
                <div className="mt-2">
                  <input
                    id="title"
                    name="title"
                    type="text"
                    className="block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={user.title}
                  />
                </div>
              </div>

              <div className="col-span-full">
                <label
                  htmlFor="company"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Unternehmen
                </label>
                <div className="mt-2">
                  <input
                    id="company"
                    name="company"
                    type="text"
                    className="block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={user.company}
                  />
                </div>
              </div>

              <div className="col-span-full">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  E-Mail-Adresse
                </label>
                <div className="mt-2">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className="block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={user.email}
                  />
                </div>
              </div>
            </div>

            <div className="mt-8 flex">
              <button
                type="submit"
                name="intent"
                value="allgemeine_einstellungen"
                className="rounded-md bg-svisit-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-500"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Speichert...' : 'Speichern'}
              </button>
            </div>
          </Form>
        </div>

        {/* Lieferadresse */}

        <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Lieferadresse
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-700">
              Verwalte die Lieferadresse deines Accounts.
            </p>
          </div>

          <Form method="POST" className="md:col-span-2">
            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:max-w-xl sm:grid-cols-6">
              <div className="sm:col-span-3">
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Vorname
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="firstName"
                    id="firstName"
                    autoComplete="given-name"
                    className="capitalize block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300  focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={userAddress.firstName}
                  />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Nachname
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="lastName"
                    id="lastName"
                    autoComplete="family-name"
                    className="capitalize block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={userAddress.lastName || ''}
                  />
                </div>
              </div>

              <div className="col-span-full">
                <label
                  htmlFor="company"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Firma (optional)
                </label>
                <div className="mt-2">
                  <input
                    id="company"
                    name="company"
                    type="text"
                    className="block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={userAddress.company || ''}
                  />
                </div>
              </div>

              <div className="col-span-full">
                <label
                  htmlFor="address"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Adresse
                </label>
                <div className="mt-2">
                  <input
                    id="address"
                    name="address"
                    type="address"
                    autoComplete="address"
                    className="block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={userAddress.address || ''}
                  />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="city"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Stadt
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="city"
                    id="city"
                    autoComplete="given-name"
                    className="capitalize block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300  focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={userAddress.city || ''}
                  />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="country"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Land
                </label>
                <div className="mt-2">
                  <select
                    id="country"
                    name="country"
                    autoComplete="country-name"
                    className="block w-full rounded-md border-gray-300 shadow-xs focus:border-svisit-primary-500 focus:ring-svisit-primary-500 sm:text-sm"
                  >
                    <option>Schweiz</option>
                    {/* <option>Deutschland</option>
                <option>Österreich</option> */}
                  </select>
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="zip"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Postleitzahl
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="zip"
                    id="zip"
                    autoComplete="given-name"
                    className="capitalize block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300  focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={userAddress.zip || ''}
                  />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Telefonnummer
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="phone"
                    id="phone"
                    autoComplete="family-name"
                    className="capitalize block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={userAddress.phone || ''}
                  />
                </div>
              </div>
            </div>

            {/* Form switch, dass in Action unterschieden werden kann */}

            {data.userAddress.length == 0 ? (
              <input name="existingAddress" hidden defaultValue="true" />
            ) : (
              ''
            )}

            <div className="mt-8 flex">
              <button
                type="submit"
                name="intent"
                value="lieferadresse"
                className="rounded-md bg-svisit-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-500"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Speichert...' : 'Speichern'}
              </button>
            </div>
          </Form>
        </div>

        <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Passwort ändern
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-700">
              Verwalte das Passwort deines Accounts.
            </p>
          </div>

          <div className="md:col-span-2">
            <div className="flex">
              <button
                type="button"
                className="rounded-md bg-svisit-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-500 disabled:cursor-not-allowed disabled:bg-gray-300"
                onClick={handlePasswordReset}
                disabled={buttonDisabled}
              >
                {buttonDisabled
                  ? 'Link wurde gesendet, bitte prüfe dein Postfach'
                  : ' Link zum Zurücksetzen des Passworts anfordern'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Settings;
